import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../../animations';

/**
 * A single hairline of progress under the nav, driven straight from scroll.
 *
 * It renders as a full-width bar collapsed to zero with a transform, never by
 * animating width — width would force a layout pass on every scroll tick, which
 * is exactly the jank this element exists to not cause. transform scales on the
 * compositor, so the line stays smooth even while a canvas page is drawing.
 *
 * Deliberately quiet: a 2px rule in the accent, faded to a whisper until the
 * page is long enough to be worth measuring. Reduced-motion removes the easing
 * so it tracks the thumb 1:1.
 */
const ScrollProgress: React.FC = () => {
    const barRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();
    const raf = useRef(0);
    const current = useRef(0);
    const target = useRef(0);

    useEffect(() => {
        const wrap = wrapRef.current;
        const bar = barRef.current;
        if (!wrap || !bar) return;

        const measure = () => {
            const doc = document.documentElement;
            const scrollable = doc.scrollHeight - window.innerHeight;
            // Hide entirely on short pages — a full bar on a one-screen page
            // is a progress indicator with nothing to say.
            const usable = scrollable > window.innerHeight * 1.5;
            wrap.style.opacity = usable ? '1' : '0';
            target.current = usable && scrollable > 0
                ? Math.min(1, Math.max(0, window.scrollY / scrollable))
                : 0;
        };

        const tick = () => {
            // Ease toward the target so the line glides rather than steps,
            // unless the user asked for no motion.
            current.current = reduced
                ? target.current
                : current.current + (target.current - current.current) * 0.18;
            bar.style.transform = `scaleX(${current.current})`;
            raf.current = requestAnimationFrame(tick);
        };

        measure();
        window.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        raf.current = requestAnimationFrame(tick);
        return () => {
            window.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
            cancelAnimationFrame(raf.current);
        };
    }, [reduced]);

    return (
        <div
            ref={wrapRef}
            aria-hidden
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, height: 2,
                zIndex: 1001, pointerEvents: 'none', opacity: 0,
                transition: 'opacity 300ms ease',
            }}
        >
            <div
                ref={barRef}
                style={{
                    height: '100%', width: '100%',
                    background: 'var(--bs-accent)',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    willChange: 'transform',
                }}
            />
        </div>
    );
};

export default ScrollProgress;
